import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/components/reveal";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      { title: "Reset password — Pluggz" },
      { name: "description", content: "Reset your Pluggz account password to regain access to your creator storefront, commissions, wishlist, and saved shopping picks." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/forgot-password` }],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    setLoading(false);
    if (error) return setError(error.message);
    setMessage("Check your inbox for a reset link.");
  }

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        <p className="editorial-eyebrow">Account recovery</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          Reset <span className="italic shimmer">password</span>
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <div>
            <label htmlFor="forgot-email" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <input
              id="forgot-email"
              required
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 text-base outline-none transition-colors focus:border-foreground sm:text-sm"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {message && <p className="text-xs text-accent">{message}</p>}
          <button
            disabled={loading}
            type="submit"
            className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          Remember it? <Link to="/login" className="link-underline text-foreground">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
